import React, { useEffect, useState } from 'react';
import {
  ChatBubbleLeftRightIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClockIcon,
  PhoneIcon,
  PlusIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import PhoneNumberList from './PhoneNumberList';
import Button from '../UI/Button';
import ErrorMessage from '../UI/ErrorMessage';
import { PhoneNumber } from '../../types/campaign';
import { smsService } from '../../services/smsService';
import { SmsGlobalTemplate } from '../../types/sms';
import { SMS_ELIGIBLE_LEAD_STATUSES } from '../../constants/smsEligibleStatuses';
import {
  CAMPAIGN_TIMEZONE_OPTIONS,
  formatCampaignTimezoneLabel,
  leadBucketForCampaignTimezone,
} from '../../constants/campaignTimezones';
import { ScheduleTimeRange, formatScheduleTime12 } from './ScheduleTimeInput';

export type SmsSendScheduleForm = {
  startTime: string;
  endTime: string;
  timezone: string;
};

export type SmsSequenceStepForm = {
  stepNumber: number;
  templateBody: string;
  delayHours: number;
};

type DelayUnit = 'hours' | 'days';

function toDisplayDelay(hours: number): { value: number; unit: DelayUnit } {
  if (hours >= 24 && hours % 24 === 0) {
    return { value: hours / 24, unit: 'days' };
  }
  return { value: hours, unit: 'hours' };
}

function fromDisplayDelay(value: number, unit: DelayUnit): number {
  const n = Math.max(0, Math.floor(value) || 0);
  return unit === 'days' ? n * 24 : n;
}

/** Compact select for toolbar rows — overrides .select w-full/py-3 so text is not truncated to dots. */
const COMPACT_SELECT_CLASS =
  'select !w-auto shrink-0 min-w-[7.5rem] text-xs text-foreground h-9 !py-1.5 px-3';
const FULL_SELECT_CLASS =
  'select w-full text-xs text-foreground h-9 !py-1.5 px-3';

function formatDelayLabel(hours: number): string {
  if (hours <= 0) return 'immediately after the previous message';
  if (hours % 24 === 0 && hours >= 24) {
    const d = hours / 24;
    return `${d} day${d === 1 ? '' : 's'} after the previous message (if no reply)`;
  }
  return `${hours} hour${hours === 1 ? '' : 's'} after the previous message (if no reply)`;
}

interface SmsCampaignSetupFieldsProps {
  sequenceSteps: SmsSequenceStepForm[];
  onSequenceStepsChange: (steps: SmsSequenceStepForm[]) => void;
  onFirstStepMessageChange: (body: string) => void;
  phoneNumberIds: string[];
  onPhoneNumberIdsChange: (ids: string[]) => void;
  displayPhoneNumbers: PhoneNumber[];
  loadingPhoneNumbers: boolean;
  phoneNumberError?: string;
  phoneNumbersEmptyHint?: string;
  sequenceStepsError?: string;
  warmupStartVolume: number;
  warmupEndVolume: number;
  warmupDays: number;
  onWarmupChange: (field: 'warmupStartVolume' | 'warmupEndVolume' | 'warmupDays', value: number) => void;
  sendSchedules: SmsSendScheduleForm[];
  onSendSchedulesChange: (schedules: SmsSendScheduleForm[]) => void;
  sendSchedulesError?: string;
  sendSchedulesDisabled?: boolean;
  eligibleLeadStatuses: string[];
  onEligibleLeadStatusesChange: (statuses: string[]) => void;
  eligibleLeadStatusesError?: string;
  disabled?: boolean;
}

const SmsCampaignSetupFields: React.FC<SmsCampaignSetupFieldsProps> = ({
  sequenceSteps,
  onSequenceStepsChange,
  onFirstStepMessageChange,
  phoneNumberIds,
  onPhoneNumberIdsChange,
  displayPhoneNumbers,
  loadingPhoneNumbers,
  phoneNumberError,
  phoneNumbersEmptyHint,
  sequenceStepsError,
  warmupStartVolume,
  warmupEndVolume,
  warmupDays,
  onWarmupChange,
  sendSchedules,
  onSendSchedulesChange,
  sendSchedulesError,
  sendSchedulesDisabled = false,
  eligibleLeadStatuses,
  onEligibleLeadStatusesChange,
  eligibleLeadStatusesError,
  disabled = false,
}) => {
  const [warmupOpen, setWarmupOpen] = useState(false);
  const [messageTemplates, setMessageTemplates] = useState<SmsGlobalTemplate[]>([]);
  const [sequenceTemplates, setSequenceTemplates] = useState<SmsGlobalTemplate[]>([]);

  useEffect(() => {
    smsService
      .getGlobalTemplates()
      .then((data) => {
        setMessageTemplates(data.message || []);
        setSequenceTemplates(data.sequence || []);
      })
      .catch(() => undefined);
  }, []);

  const applySequenceTemplate = (templateId: string) => {
    if (!templateId) return;
    const template = sequenceTemplates.find((t) => t.id === templateId);
    if (!template?.steps?.length) return;

    const hasExistingText = sequenceSteps.some((s) => s.templateBody.trim());
    if (
      hasExistingText &&
      !window.confirm('Replace the current message sequence with this template?')
    ) {
      return;
    }

    const nextSteps = template.steps.map((s) => ({
      stepNumber: s.stepNumber,
      templateBody: s.templateBody,
      delayHours: s.delayHours,
    }));
    onSequenceStepsChange(nextSteps);
    const first = nextSteps.find((s) => s.stepNumber === 1);
    if (first) onFirstStepMessageChange(first.templateBody);
  };

  const applyMessageTemplate = (stepNumber: number, templateId: string) => {
    if (!templateId) return;
    const template = messageTemplates.find((t) => t.id === templateId);
    const body = template?.steps?.[0]?.templateBody;
    if (body) updateStep(stepNumber, { templateBody: body });
  };

  const addStep = () => {
    const next = (sequenceSteps[sequenceSteps.length - 1]?.stepNumber || 0) + 1;
    onSequenceStepsChange([
      ...sequenceSteps,
      { stepNumber: next, templateBody: '', delayHours: 24 },
    ]);
  };

  const removeStep = (stepNumber: number) => {
    onSequenceStepsChange(
      sequenceSteps
        .filter((s) => s.stepNumber !== stepNumber)
        .map((s, i) => ({ ...s, stepNumber: i + 1 }))
    );
  };

  const updateStep = (stepNumber: number, patch: Partial<SmsSequenceStepForm>) => {
    const next = sequenceSteps.map((s) =>
      s.stepNumber === stepNumber ? { ...s, ...patch } : s
    );
    onSequenceStepsChange(next);
    if (stepNumber === 1 && patch.templateBody !== undefined) {
      onFirstStepMessageChange(patch.templateBody);
    }
  };

  const updateStepDelay = (stepNumber: number, value: number, unit: DelayUnit) => {
    updateStep(stepNumber, { delayHours: fromDisplayDelay(value, unit) });
  };

  const toggleEligibleStatus = (status: string) => {
    if (disabled) return;
    if (eligibleLeadStatuses.includes(status)) {
      onEligibleLeadStatusesChange(eligibleLeadStatuses.filter((s) => s !== status));
    } else {
      onEligibleLeadStatusesChange([...eligibleLeadStatuses, status]);
    }
  };

  const selectAllEligibleStatuses = () => {
    if (disabled) return;
    onEligibleLeadStatusesChange(SMS_ELIGIBLE_LEAD_STATUSES.map((s) => s.value));
  };

  const clearEligibleStatuses = () => {
    if (disabled) return;
    onEligibleLeadStatusesChange([]);
  };

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-primary/20 bg-primary/5 p-4">
        <div className="flex gap-3">
          <InformationCircleIcon className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
          <div className="text-sm text-foreground space-y-1">
            <p className="font-medium">How SMS campaigns work</p>
            <ul className="list-disc list-inside text-muted-foreground space-y-0.5">
              <li>Message 1 goes out when you start the campaign.</li>
              <li>Follow-ups send only if the lead has not replied.</li>
              <li>Any reply stops the sequence and opens the thread in SMS Inbox.</li>
            </ul>
          </div>
        </div>
      </div>

      <section className="space-y-3">
        <div className="flex items-center gap-2">
          <PhoneIcon className="h-5 w-5 text-muted-foreground" />
          <h3 className="text-sm font-semibold text-foreground">Sending numbers</h3>
        </div>
        <p className="text-xs text-muted-foreground">
          Select Telnyx numbers enabled for SMS. Voice-only numbers are hidden.
        </p>
        <PhoneNumberList
          phoneNumbers={displayPhoneNumbers}
          selectedPhoneNumberIds={phoneNumberIds}
          onSelectionChange={onPhoneNumberIdsChange}
          loading={loadingPhoneNumbers}
          error={phoneNumberError}
          smsMode
          emptyHint={phoneNumbersEmptyHint}
        />
        {displayPhoneNumbers.length > 0 &&
          displayPhoneNumbers.every((p) => !p.smsCapable) && (
            <p className="text-xs text-amber-600 dark:text-amber-400">
              Numbers loaded from Telnyx, but none are marked SMS-ready. Assign a messaging profile to each number in Telnyx, then refresh.
            </p>
          )}
      </section>

      <section className="space-y-3" data-field="eligible-lead-statuses">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-foreground">SMS allow-list — send to leads with status *</h3>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="text-xs text-primary hover:underline disabled:opacity-50"
              onClick={selectAllEligibleStatuses}
              disabled={disabled}
            >
              Select all
            </button>
            <span className="text-muted-foreground">|</span>
            <button
              type="button"
              className="text-xs text-muted-foreground hover:underline disabled:opacity-50"
              onClick={clearEligibleStatuses}
              disabled={disabled}
            >
              Clear all
            </button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          SMS campaigns only. Choose which lead statuses receive automated sequence messages. DNC leads are
          always excluded, even if their status matches.
        </p>
        <div className="rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-950/30 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
          Do Not Call (DNC) leads never receive automated SMS, regardless of selection.
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
          {SMS_ELIGIBLE_LEAD_STATUSES.map((option) => {
            const checked = eligibleLeadStatuses.includes(option.value);
            return (
              <label
                key={option.value}
                className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm cursor-pointer transition-colors ${
                  checked
                    ? 'border-primary bg-primary/5 text-foreground'
                    : 'border-border bg-card text-muted-foreground hover:border-primary/40'
                } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                <input
                  type="checkbox"
                  className="rounded border-border text-primary focus:ring-primary"
                  checked={checked}
                  disabled={disabled}
                  onChange={() => toggleEligibleStatus(option.value)}
                />
                <span>{option.label}</span>
              </label>
            );
          })}
        </div>
        <ErrorMessage message={eligibleLeadStatusesError} />
      </section>

      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <ChatBubbleLeftRightIcon className="h-5 w-5 text-muted-foreground" />
            <h3 className="text-sm font-semibold text-foreground">Message sequence</h3>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {sequenceTemplates.length > 0 && (
              <label className="inline-flex items-center gap-2 shrink-0">
                <span className="text-xs text-muted-foreground whitespace-nowrap">Sequence template</span>
                <select
                  className={`${COMPACT_SELECT_CLASS} min-w-[9rem] max-w-[14rem]`}
                  defaultValue=""
                  onChange={(e) => {
                    applySequenceTemplate(e.target.value);
                    e.target.value = '';
                  }}
                >
                  <option value="">Choose</option>
                  {sequenceTemplates.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name} ({t.steps.length} steps)
                    </option>
                  ))}
                </select>
              </label>
            )}
            <Button type="button" variant="secondary" size="sm" onClick={addStep}>
              <PlusIcon className="h-4 w-4 mr-1" />
              Add follow-up
            </Button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Personalize with{' '}
          <code className="text-xs bg-muted px-1 rounded">{'{{firstName}}'}</code>,{' '}
          <code className="text-xs bg-muted px-1 rounded">{'{{lastName}}'}</code>,{' '}
          <code className="text-xs bg-muted px-1 rounded">{'{{companyName}}'}</code>
          . Selecting a template fills the message — you can edit it before saving the campaign.
        </p>

        <div className="space-y-4">
          {sequenceSteps.map((step) => {
            const isFirst = step.stepNumber === 1;
            const delayDisplay = toDisplayDelay(step.delayHours);

            return (
              <div
                key={step.stepNumber}
                className="rounded-lg border border-border bg-card p-4 relative"
              >
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div>
                    <span className="inline-flex items-center rounded-full bg-primary/10 text-primary px-2.5 py-0.5 text-xs font-semibold">
                      {isFirst ? 'Message 1 — initial outreach' : `Follow-up ${step.stepNumber - 1}`}
                    </span>
                    <p className="text-xs text-muted-foreground mt-1">
                      {isFirst
                        ? 'Sent when the campaign starts (if lead is eligible).'
                        : formatDelayLabel(step.delayHours)}
                    </p>
                  </div>
                  {!isFirst && (
                    <button
                      type="button"
                      className="text-xs text-destructive hover:underline"
                      onClick={() => removeStep(step.stepNumber)}
                    >
                      Remove
                    </button>
                  )}
                </div>

                {messageTemplates.length > 0 && (
                  <div className="mb-2">
                    <label className="block text-xs text-muted-foreground mb-1">Use global template</label>
                    <select
                      className={`${FULL_SELECT_CLASS} max-w-md`}
                      defaultValue=""
                      onChange={(e) => {
                        applyMessageTemplate(step.stepNumber, e.target.value);
                        e.target.value = '';
                      }}
                    >
                      <option value="">Choose template</option>
                      {messageTemplates.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <label className="block text-xs font-medium text-foreground mb-1">
                  Message text *
                </label>
                <textarea
                  className="textarea min-h-[88px]"
                  value={step.templateBody}
                  onChange={(e) => updateStep(step.stepNumber, { templateBody: e.target.value })}
                  placeholder={
                    isFirst
                      ? 'Hi {{firstName}}, we received your inquiry...'
                      : 'Just following up — still interested?'
                  }
                />

                {!isFirst && (
                  <div className="mt-3 flex flex-wrap items-end gap-2">
                    <ClockIcon className="h-4 w-4 text-muted-foreground mb-2" />
                    <div className="flex-1 min-w-[120px]">
                      <label className="block text-xs text-muted-foreground mb-1">Wait before sending</label>
                      <input
                        type="number"
                        className="input"
                        min={0}
                        value={delayDisplay.value}
                        onChange={(e) =>
                          updateStepDelay(
                            step.stepNumber,
                            Number(e.target.value) || 0,
                            delayDisplay.unit
                          )
                        }
                      />
                    </div>
                    <div className="w-28">
                      <label className="block text-xs text-muted-foreground mb-1">Unit</label>
                      <select
                        className={FULL_SELECT_CLASS}
                        value={delayDisplay.unit}
                        onChange={(e) =>
                          updateStepDelay(
                            step.stepNumber,
                            delayDisplay.value,
                            e.target.value as DelayUnit
                          )
                        }
                      >
                        <option value="hours">Hours</option>
                        <option value="days">Days</option>
                      </select>
                    </div>
                    <p className="w-full text-xs text-muted-foreground">
                      Only sends if the lead has not replied to earlier messages.
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <ErrorMessage message={sequenceStepsError} />
      </section>

      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <ClockIcon className="h-4 w-4 text-muted-foreground" />
              Daily send windows
            </h3>
            <p className="text-xs text-muted-foreground mt-1">
              Each window targets leads in that timezone only. Example: an Eastern window 8–10 AM sends
              only to Eastern-timezone leads during that window. Leave empty to send any time to all leads.
            </p>
          </div>
          {!sendSchedulesDisabled && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() =>
                onSendSchedulesChange([
                  ...sendSchedules,
                  {
                    startTime: '08:00',
                    endTime: '10:00',
                    timezone: 'America/New_York',
                  },
                ])
              }
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              Add send window
            </Button>
          )}
        </div>

        {sendSchedules.length === 0 ? (
          <p className="text-xs text-muted-foreground rounded-lg border border-dashed border-border px-4 py-3">
            No send windows configured — messages can go out any time while the campaign is active.
          </p>
        ) : (
          <div className="space-y-3">
            {sendSchedules.map((schedule, index) => (
              <div
                key={`send-schedule-${index}`}
                className="rounded-lg border border-border bg-card p-4 space-y-3"
              >
                <div className="flex items-start justify-between gap-2">
                  <span className="text-xs font-semibold text-foreground">Window {index + 1}</span>
                  {!sendSchedulesDisabled && (
                    <button
                      type="button"
                      className="text-xs text-destructive hover:underline"
                      onClick={() =>
                        onSendSchedulesChange(sendSchedules.filter((_, i) => i !== index))
                      }
                    >
                      Remove
                    </button>
                  )}
                </div>
                <div className="rounded-lg border border-border/80 bg-muted/20 p-3 space-y-3">
                  <ScheduleTimeRange
                    startTime={schedule.startTime}
                    endTime={schedule.endTime}
                    disabled={sendSchedulesDisabled || disabled}
                    idPrefix={`send-window-${index}`}
                    onStartChange={(startTime) => {
                      const next = [...sendSchedules];
                      next[index] = { ...schedule, startTime };
                      onSendSchedulesChange(next);
                    }}
                    onEndChange={(endTime) => {
                      const next = [...sendSchedules];
                      next[index] = { ...schedule, endTime };
                      onSendSchedulesChange(next);
                    }}
                  />
                  <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-3">
                    <label
                      htmlFor={`send-window-tz-${index}`}
                      className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground shrink-0"
                    >
                      Timezone
                    </label>
                    <select
                      id={`send-window-tz-${index}`}
                      className={`${FULL_SELECT_CLASS} sm:max-w-xs`}
                      value={schedule.timezone || 'America/New_York'}
                      disabled={sendSchedulesDisabled || disabled}
                      onChange={(e) => {
                        const next = [...sendSchedules];
                        next[index] = { ...schedule, timezone: e.target.value };
                        onSendSchedulesChange(next);
                      }}
                    >
                      {CAMPAIGN_TIMEZONE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                {schedule.startTime && schedule.endTime && (
                  <p className="text-xs text-muted-foreground flex items-start gap-1.5">
                    <ClockIcon className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    <span>
                      Sends daily{' '}
                      <span className="font-medium text-foreground">
                        {formatScheduleTime12(schedule.startTime)} – {formatScheduleTime12(schedule.endTime)}
                      </span>{' '}
                      {formatCampaignTimezoneLabel(schedule.timezone)}
                      {leadBucketForCampaignTimezone(schedule.timezone)
                        ? ` — ${leadBucketForCampaignTimezone(schedule.timezone)} leads only`
                        : '.'}
                    </span>
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
        <ErrorMessage message={sendSchedulesError} />
      </section>

      <section className="rounded-lg border border-border">
        <button
          type="button"
          className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-muted/40 rounded-lg transition-colors"
          onClick={() => setWarmupOpen((o) => !o)}
        >
          <div>
            <span className="text-sm font-semibold text-foreground">Daily volume ramp (optional)</span>
            <span className="ml-2 inline-flex items-center rounded-full bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide">
              Enforced
            </span>
          </div>
          {warmupOpen ? (
            <ChevronUpIcon className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronDownIcon className="h-4 w-4 text-muted-foreground" />
          )}
        </button>
        {warmupOpen && (
          <div className="px-4 pb-4 space-y-3 border-t border-border pt-3">
            <p className="text-xs text-muted-foreground">
              Per sending number: start low and ramp up over several days. Sends over the daily cap are deferred automatically.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground mb-1">Day 1 cap (SMS/day)</label>
                <input
                  type="number"
                  className="input"
                  min={1}
                  value={warmupStartVolume}
                  onChange={(e) => onWarmupChange('warmupStartVolume', Number(e.target.value) || 1)}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground mb-1">Final cap (SMS/day)</label>
                <input
                  type="number"
                  className="input"
                  min={1}
                  value={warmupEndVolume}
                  onChange={(e) => onWarmupChange('warmupEndVolume', Number(e.target.value) || 1)}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground mb-1">Ramp over (days)</label>
                <input
                  type="number"
                  className="input"
                  min={1}
                  value={warmupDays}
                  onChange={(e) => onWarmupChange('warmupDays', Number(e.target.value) || 1)}
                />
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default SmsCampaignSetupFields;
